import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import localeText from "../../../../locale";
import DashboardLayout from "../../layouts/DashboardLayout";
import Loading from "../../../../components/Loading/Loading";
import useDashboardData from "../../hooks/useDashboardData";
import useCurrency from "../../../../hooks/useCurrency";
import { formatPrice } from "../../../../utilities/formatPrice";
import KPICard from "../../components/shared/KPICard/KPICard";
import PatrimonyCard from "./components/PatrimonyCard";
import WalletCard from "./components/WalletCard";
import ExtracreditCard from "./components/ExtracreditCard";
import ExtrapayProgramBanner from "./Extrapay/components/ExtrapayProgramBanner";
import PlatformExcCard from "./Extrapay/components/PlatformExcCard";
import useExtrapayOverview from "./Extrapay/hooks/useExtrapayOverview";
import { EXTRAPAY_WITHDRAW_PATH, extrapayTabPath } from "./Extrapay/constants";
import TransferExtracreditCard from "./components/TransferExtracreditCard/TransferExtracreditCard";
import QuickAccessCard from "../CustomerDashboard/components/QuickAccessCard";
import InviteCouponDialog from "./components/InviteCouponDialog/InviteCouponDialog";
import { normalizeExtracoinUsagePercent } from "../../utils/extracoinUsagePercent";
import QrCodePayDialog from "../../components/QrCodePay/QrCodePayDialog";
import PhysicalStoreService from "../../../../service/PhysicalStoreService";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";


const FinancialHub = ({ userTypeInfo }) => {
  const history = useHistory();
  const { data, isLoading, error, refreshData } = useDashboardData();
  const [inviteCouponVisible, setInviteCouponVisible] = useState(false);
  const [qrCodePayVisible, setQrCodePayVisible] = useState(false);
  const [isCheckingPhysicalStoreVerification, setIsCheckingPhysicalStoreVerification] =
    useState(false);
  const { currencySymbol } = useCurrency(data?.walletInfo?.currency);
  const { showErrorToast } = useToastContext();
  const extrapay = useExtrapayOverview();
  const isCustomer = userTypeInfo === "Customer";
  const physicalStoreService = new PhysicalStoreService();

  const extracoinBalance = Number(data?.extracoinInfo?.balance ?? 0) || 0;
  const extracreditUsagePercent = normalizeExtracoinUsagePercent(data?.extracoinPercentage);
  const extracreditEquivalentSubtitle = useMemo(
    () =>
      localeText("customerHomeExtracreditEquivalent", {
        value: `${currencySymbol} ${formatPrice(extracoinBalance)}`,
      }) || `${currencySymbol} ${formatPrice(extracoinBalance)}`,
    [currencySymbol, extracoinBalance],
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <Loading classNameIcon="text-8xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <p className="text-red-500">Erro ao carregar dados.</p>
        </div>
      </DashboardLayout>
    );
  }

  const walletBalance = data.walletInfo?.balance ?? 0;
  const consolidatedPatrimony = walletBalance + extracoinBalance * 0.5;
  const changeLast30Days = null;

  const handleWalletWithdraw = () => history.push("/dashboard-financeiro/carteira");
  const handleWalletStatement = () => history.push("/dashboard-financeiro/carteira?tab=transacoes");
  const handleExtracreditUseInStore = () => history.push("/shop");
  const handleExtracreditStatement = () => history.push("/dashboard-financeiro/extracredit");

  const handlePaymentsPhysicalStoreClick = async () => {
    setIsCheckingPhysicalStoreVerification(true);
    try {
      const res = await physicalStoreService.verificationStatus(data.userInfo.username);

      const isPending =
        res.messageCode === 500253 ||
        (res.messageCode === 200001 && res.result?.verificationStatus === "PENDING") ||
        (res.messageCode === 200001 && res.result?.verificationStatus === "REJECTED");

      if (isPending) {
        history.push("/dashboard-store/physical-store");
        return;
      }

      history.push("/dashboard-store-transactions");
    } catch (error) {
      console.error("Error checking verification:", error);
      showErrorToast({ code: 500000 });
    } finally {
      setIsCheckingPhysicalStoreVerification(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex align-items-center gap-4">
        <div
          className="border-circle p-3"
          style={{
            border: "1px solid var(--theme-border, #EBEBEB)",
            width: "54px",
            height: "54px",
          }}
        >
          <i
            className="pi pi-wallet text-xl"
            style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
          />
        </div>
        <div>
          <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
            {localeText("centralFinanceira")}
          </h2>
          <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("financialHubSubtitle")}
          </p>
        </div>
      </div>
      <hr className="mb-5" style={{ borderColor: "var(--theme-border, #EBEBEB)" }} />

      <ExtrapayProgramBanner
        isLoading={extrapay.isLoading}
        hasError={extrapay.hasError}
        onRetry={extrapay.reload}
        pointsBalance={extrapay.pointsBalance}
        ordersInCycle={extrapay.ordersInCycle}
        cycle={extrapay.cycle}
        conversionRate={extrapay.conversionRate}
      />

      {/* {!isCustomer && (
        <PatrimonyCard
          totalAmount={consolidatedPatrimony}
          changeLast30Days={changeLast30Days}
          currencySymbol={currencySymbol}
        />
      )}

      {isCustomer && (
        <KPICard
          title={localeText("customerHomeExtracreditBalanceTitle") || "Extracredit"}
          value={extracoinBalance}
          subtitle={extracreditEquivalentSubtitle}
          valueLabel="ECR"
          icon="pi pi-wallet"
          customBgColor="var(--theme-primary-dark, #346958)"
          customTextColor="rgba(255,255,255,0.95)"
          customSubtitleColor="rgba(255,255,255,0.85)"
        />
      )} */}

      <div className="grid mb-4">
        <div className="col-12 md:col-4">
          <WalletCard
            balance={walletBalance}
            currencySymbol={currencySymbol}
            /* changeLabel="+12% este mês" */
            onWithdraw={handleWalletWithdraw}
            onStatement={handleWalletStatement}
          />
        </div>
        <div className="col-12 md:col-4">
          <ExtracreditCard
            balance={extracoinBalance}
            usagePercent={extracreditUsagePercent}
            onUseInStore={handleExtracreditUseInStore}
            onStatement={handleExtracreditStatement}
          />
        </div>
        <div className="col-12 md:col-4">
          <PlatformExcCard
            balance={extrapay.excBalance}
            isLoading={extrapay.isLoading}
            disabled={extrapay.isLoading || extrapay.hasError || extrapay.excBalance <= 0}
            onWithdraw={() => history.push(EXTRAPAY_WITHDRAW_PATH)}
            onStatement={() => history.push(extrapayTabPath("exc"))}
          />
        </div>
      </div>

      <div className="mb-4">
        <h3 className="text-base font-semibold mb-3" style={{ color: "var(--theme-text)" }}>
          {localeText("quickOperations")}
        </h3>
        <div className="flex gap-2 flex-wrap">
          <TransferExtracreditCard />
          {userTypeInfo !== "Customer" && (
            <QuickAccessCard
              icon="pi pi-gift"
              label={localeText("generateCoupon")}
              onClick={() => setInviteCouponVisible(true)}
            />
          )}
          {["Store", "Manager"].includes(userTypeInfo) && (
            <QuickAccessCard
              icon="pi pi-qrcode"
              label={localeText("paymentsPysicalStore")}
              onClick={
                isCheckingPhysicalStoreVerification ? undefined : handlePaymentsPhysicalStoreClick
              }
            />
          )}
          {["Store", "Manager"].includes(userTypeInfo) && (
            <QuickAccessCard
              icon="pi pi-camera"
              label={localeText("payPhysicalStore")}
              onClick={() => setQrCodePayVisible(true)}
            />
          )}
        </div>
      </div>

      {userTypeInfo !== "Customer" && (
        <InviteCouponDialog
          visible={inviteCouponVisible}
          onHide={() => setInviteCouponVisible(false)}
          extracoinBalance={extracoinBalance}
          refreshData={refreshData}
        />
      )}

      {["Store", "Manager"].includes(userTypeInfo) && (
        <QrCodePayDialog
          visible={qrCodePayVisible}
          onHide={() => setQrCodePayVisible(false)}
          user={data.userInfo}
          currency={data.walletInfo?.currency}
        />
      )}
    </DashboardLayout>
  );
};

export default FinancialHub;
